import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';

import { useNavigate, useParams } from 'react-router-dom';
import { SelectableBoxCommon } from 'modules/Users/components';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import ContentStructureField from './ContentStructureField';
import SourceOfKnowledgeField from './SourceOfKnowledgeField';
import WhattoExpectField from './WhattoExpectField';
import SummaryField from './SummaryField';
import PricingField from './PricingField';
import Promotions from './Promotions';
import ContentThumbnail from './ContentThumbnail';
import { ConfirmModal } from 'components';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAppData } from 'context/AppContext';
import { Content } from 'types';
import swal from 'sweetalert';
import { Content as ContentType } from "types";
import { Timestamp } from 'firebase/firestore';
import CategoryField from './CategoryField';
import ContentTitleField from './ContentTitleField';
import AgreeToTerms from './AgreeToTerms';
import { useAuth } from 'context/authContext';
import { ContentsService } from 'services/ContentsService';


type SelectedValueType =
  | "contentstructure"
  | "sourceofknowledge"
  | "category"
  | "contentTitle"
  | "summary"
  | "whattoexpect"
  | "pricing"
  | "promotions"
  | "contentthumbnail"
  | "agreeToTerms";

const ContentEditData = () => {
  const { currentUser } = useAuth();
  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>('contentstructure');
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [content, setContent] = useState<ContentType | null>({
    id: "",
    createdBy: null,
    type: "handout",
    category: "Psychotherapy",
    title: "",
    coverPhotoUrl: "",
    isPublished: false,
    summary: "",
    expectations: "",
    sourceOfKnowledge: "Clinical Insights",
    price: 0,
    promotion: {
      isActive: false,
      discountPercent: 0,
      isFree: false,
      code: "",
    },
    purchaseCount: 0,
    revenue: 0,
    currentStatus: {
      status: "draft",
      date: Timestamp.now(),
    },
    statusHistory: [
      {
        status: "draft",
        date: Timestamp.now(),
      },
    ],
    contentStructure: [],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  const { fetchContentById, updateContentById } = useAppData();
  const { id } = useParams();
  const [isAgreed, setIsAgreed] = useState(false);

  const [showIcons, setShowIcons] = useState({
    contentstructure: false,
    sourceofknowledge: false,
    category: false,
    contentTitle: false,
    summary: false,
    whattoexpect: false,
    pricing: false,
    promotions: false,
    contentthumbnail: false,
    agreeToTerms: false,
  });



  useEffect(() => {
    if (id) {
      fetchContentById(id).then((data: any) => {
        setContent(data.data);
        updateShowIcons(data.data);
      });

    }
  }, [id]);

  const updateShowIcons = (data: ContentType) => {
    setShowIcons({
      contentstructure: data.contentStructure.length > 0,
      sourceofknowledge: !!data.sourceOfKnowledge,
      category: !!data.category,
      contentTitle: !!data.title,
      summary: !!data.summary,
      whattoexpect: !!data.expectations,
      pricing: data.price > 0,
      promotions: data.promotion.isActive || data.price > 0,
      contentthumbnail: !!data.coverPhotoUrl,
      agreeToTerms: isAgreed, // or any condition you have
    });
  };



  const handleOpenModal = () => setConfirmModalOpen(true);
  const handleCloseModal = () => setConfirmModalOpen(false);

  const handleConfirm = async () => {
    if (content) {
      try {
        const response = await updateContentById(content.id, content);
        if (response.success) {
          swal({
            icon: 'success',
            title: 'Success',
            text: 'Content updated successfully!',
          });
        } else {
          swal({
            icon: 'error',
            title: 'Error',
            text: response.message,
          });
        }
      } catch (error) {
        swal({
          icon: 'error',
          title: 'Error',
          text: error instanceof Error ? error.message : 'An unexpected error occurred',
        });
      }
    }
    setConfirmModalOpen(false);
  };

  // const handleConfirm = async () => {
  //   try {
  //     const id = currentUser?.uid;

  //     if (!id) {
  //       console.error("User is not authenticated");
  //       return;
  //     }

  //     if (mode === "edit" && contentData.id) {
  //       // Update existing handout
  //       await handleUpdateHandout(contentData.id, contentData);
  //       console.log("Handout updated successfully");
  //     } else if (mode === "add") {
  //       // Create a new handout
  //       await ContentsService.createContents(id, [contentData]);
  //       console.log("Handout created successfully");
  //     }

  //     // Navigate after update or create
  //     navigate("/my-content");
  //   } catch (error) {
  //     console.error("Failed to save handout:", error);
  //   } finally {
  //     setConfirmModalOpen(false);
  //   }
  // };


  const steps: SelectedValueType[] = [
    'contentstructure',
    'sourceofknowledge',
    'whattoexpect',
    'summary',
    'pricing',
    'promotions',
    'contentthumbnail',
  ];

  const headers: Record<SelectedValueType, string> = {
    contentstructure: "Content Structure",
    sourceofknowledge: "Source of Knowledge",
    category: "Category",
    contentTitle: "Content Title",
    summary: "Summary",
    whattoexpect: "What to Expect",
    pricing: "Pricing",
    promotions: "Promotions",
    contentthumbnail: "Content Thumbnail",
    agreeToTerms: "Agree to Terms",
  };

  const handleSelectionChange = (value: SelectedValueType) => {
    setSelectedValue(value);
  };

  const handleNextClick = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedValue(steps[currentStep + 1]);
    }
  };

  // const handlePublishContentChange = async () => {
  //   setContent((prevContent) =>
  //     prevContent
  //       ? {
  //         ...prevContent,
  //         isPublished: !prevContent.isPublished,
  //       }
  //       : null
  //   )
  // };

  const handlePublishContentChange = async () => {
    if (!content) return;

    try {
      // Toggle the `isPublished` status
      const updatedContent = {
        ...content,
        isPublished: !content.isPublished,
        updatedAt: Timestamp.now(),
      };

      // Update local state
      setContent(updatedContent);

      // Update Firestore document
      await ContentsService.updateContentIsPublished(content.id, { isPublished: updatedContent.isPublished });

      // Show success alert
      swal({
        icon: "success",
        title: "Success",
        text: `Content has been ${updatedContent.isPublished ? "published" : "unpublished"} successfully!`,
      });
    } catch (error) {
      console.error("Error updating publish status:", error);
      swal({
        icon: "error",
        title: "Error",
        text: "Failed to update content status. Please try again.",
      });
    }
  };


  const handleBackClick = () => {
    navigate('/contents');
  }

  const handleAgreeClick = () => {
    setIsAgreed(true);
    setShowIcons((prevState: any) => ({
      ...prevState,
      agreeToTerms: true,
    }));
  };

  return (
    <>
      <Grid
        display='flex'
        flexDirection='column'
        justifyContent='center'
        gap={5}
        px={5}
        pb={12}
      >
        <Box
          sx={{ cursor: 'pointer' }}
          onClick={handleBackClick}
          pt={5}
          fontWeight={500}
          fontSize='28px'
          color='#233F91'
          display='flex'
          alignItems='center'>
          <KeyboardArrowLeftIcon
            sx={{ color: '#233F91', fontSize: '28px' }}
          />
          Edit Content
        </Box>
        <Grid display='flex' justifyContent='space-between'>
          <Box
          >

          </Box>
          <Box
            display='flex'
            flexDirection={'column'}
            gap={5}
            alignItems='center'>
            <Box display='flex' alignItems='center' gap={2}>
              <Typography variant='body1' color='textSecondary'>
                Publication Date
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {/* date/date/date - time */}
                {content?.updatedAt
                  ? new Date(content.updatedAt.toDate()).toLocaleString() // Convert Timestamp to String
                  : "Not Published"}
              </Typography>
            </Box>
            <Box display='flex' alignItems='center'>
              <Typography variant='body1' color='textSecondary' mr={2}>
                Status
              </Typography>
              <FormControlLabel
                control={<Switch checked={content?.isPublished} color='success' onChange={handlePublishContentChange} />}
                label='Published'
                labelPlacement='end'
                sx={{ m: 0 }}
              />
            </Box>
          </Box>
        </Grid>
        <Box display='flex' minHeight='100vh'>
          <Box width='22%' p={2}>
            <Typography
              variant='h6'
              gutterBottom
              fontSize={18}
              fontWeight={700}
              color={'#595959'}>
              Plan Your Content
            </Typography>

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='contentstructure'
              onClick={handleSelectionChange}
              text='Content Structure'
              showIcon={showIcons.contentstructure}
            />

            <SelectableBoxCommon
              selectedValue={selectedValue}
              value='sourceofknowledge'
              onClick={handleSelectionChange}
              text='Source of Knowledge'
              showIcon={showIcons.sourceofknowledge}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="category"
              onClick={handleSelectionChange}
              text="Category"
              showIcon={showIcons.category}
            />

            <Typography
              variant='h6'
              gutterBottom
              mt={2}
              fontSize={18}
              fontWeight={700}
              color={'#595959'}>
              Create Your Content
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={1}>
              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="contentTitle"
                onClick={handleSelectionChange}
                text="Content Title"
                showIcon={showIcons.contentTitle}
              />

              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="summary"
                onClick={handleSelectionChange}
                text="Summary"
                showIcon={showIcons.summary}
              />

              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="whattoexpect"
                onClick={handleSelectionChange}
                text="What to Expect"
                showIcon={showIcons.whattoexpect}
              />
            </Box>
            <Typography
              variant='h6'
              gutterBottom
              mt={2}
              fontSize={18}
              fontWeight={700}
              color={'#595959'}>
              Publish Your Content
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={1}>
              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="pricing"
                onClick={handleSelectionChange}
                text="Pricing"
                showIcon={showIcons.pricing}
              />

              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="promotions"
                onClick={handleSelectionChange}
                text="Promotions"
                showIcon={showIcons.promotions}
              />

              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="contentthumbnail"
                onClick={handleSelectionChange}
                text="Content Thumbnail"
                showIcon={showIcons.contentthumbnail}
              />

              <SelectableBoxCommon
                selectedValue={selectedValue}
                value="agreeToTerms"
                onClick={handleSelectionChange}
                text="Agree to Terms"
                showIcon={showIcons.agreeToTerms}
              />
            </Box>

            <Box
              display={'flex'}
              justifyContent={'flex-end'}
              mt={2}
              flexDirection={'column'}
              height={'20%'}
            >
              <Button
                sx={{
                  width: "214px",
                  height: "48px",
                  p: "6px 20px",
                  backgroundColor: isAgreed ? "#233F91" : "#595959",
                  borderRadius: "15px",
                  color: isAgreed ? "white" : "white",
                  "&:hover": {
                    backgroundColor: isAgreed ? "#233F91" : "#595959",
                    color: isAgreed ? "white" : "#233F91",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#595959", // Background when disabled
                    color: "white", // Text color when disabled
                  },
                }}
                type="submit"
                disabled={!isAgreed}
                onClick={handleOpenModal}
              >
                Publish
              </Button>
              <ConfirmModal
                title='You’re About to Deactivate this Account'
                subtitle='Figma ipsum component variant main layer. Line horizontal arrange team editor thumbnail distribute. Follower figma blur connection slice plugin. Text mask hand bullet ipsum slice mask selection bullet list. Polygon align hand bold.'
                cancelText='Cancel'
                confirmText='Confirm'
                isLoading={false}
                onCancel={handleCloseModal}
                onConfirm={handleConfirm}
                isConfirmModal={isConfirmModalOpen}
                icon={
                  <ErrorOutlineIcon
                  // sx={{ color: COLORS.text.error, fontSize: 40 }}
                  />
                }
              />
            </Box>
          </Box>

          <Box
            width={{ xs: '100%', md: '80%' }}
            height={{ xs: '70vh', md: '100vh' }}
            position={'relative'}
            bgcolor={'#F8F8F8'}
            sx={{
              borderRadius: '10px',
              border: '1px solid #A9A9A9',
              overflow: 'auto',
              scrollbarWidth: "thin", // ✅ Smaller scrollbar for Firefox
              scrollbarColor: "#ccc transparent", // ✅ Custom scrollbar color for Firefox
              "&::-webkit-scrollbar": {
                width: "6px", // ✅ Reduce scrollbar width
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888", // ✅ Scrollbar color
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555", // ✅ Darker color on hover
              },
            }}>
            <Typography
              variant='h5'
              gutterBottom
              fontSize={18}
              p={3}
              fontWeight={700}
              color={'#797979'}>
              {headers[selectedValue]}
            </Typography>
            <Typography
              sx={{
                borderBottom: '1px solid #A9A9A9',
                width: '100%',
              }}></Typography>

            {selectedValue === 'contentstructure' ? (
              <ContentStructureField
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === "contentTitle" ? (
              <ContentTitleField
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === 'sourceofknowledge' ? (
              <SourceOfKnowledgeField
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === "category" ? (
              <CategoryField
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === 'summary' ? (
              <SummaryField content={content}
                setContent={setContent}
              />

            ) : selectedValue === 'whattoexpect' ? (
              <WhattoExpectField
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === 'pricing' ? (
              <PricingField
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === 'promotions' ? (
              <Promotions
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === 'contentthumbnail' ? (
              <ContentThumbnail
                content={content}
                setContent={setContent}
              />
            ) : selectedValue === "agreeToTerms" ? (
              <AgreeToTerms />
            ) : null}

            <Box

              sx={{
                position: selectedValue === 'contentstructure' ? 'sticky' : 'absolute',
                bottom: 0,
                width: '100%',
                p: 2,
                bgcolor: '#F8F8F8',
                display: 'flex',
                justifyContent: { xs: 'start', md: 'center' },

              }}>

              {selectedValue === "agreeToTerms" ? (
                <Button
                  sx={{
                    color: isAgreed ? "white" : "#595959",
                    bgcolor: isAgreed ? "#233F91" : "#DBDBDB",
                    borderRadius: "10px",
                    border: "1px solid #B3B3B3",
                    fontWeight: 400,
                    fontSize: "15px",
                    "&:hover": {
                      bgcolor: isAgreed ? "#233F91" : "#DBDBDB",
                    },
                  }}
                  onClick={handleAgreeClick}
                >
                  I Agree
                </Button>
              ) : (
                <Button
                  sx={{
                    ...styleSheet.nextBtn,
                    color: showIcons[selectedValue] ? "white" : "#595959",
                    bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                    "&:hover": {
                      bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                    },
                  }}
                  onClick={handleNextClick}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default ContentEditData;

const styleSheet = {
  nextBtn: {
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  }
};